import { useCallback, useState } from 'react';
import * as claude from '../services/claude';
import { ProfilTropEloigneError, QuotaError } from '../services/claude';
import { proposeAbonnement, messageRefusSansOffre } from '../utils/quotaRefusal';
import { usePaywallStore } from '../store/paywallStore';
import type {
  CvImproveInput,
  CvScoreInput,
  ImproveAnswer,
  ImproveQuestions,
  LinkedinInput,
  CvOfferInput,
} from '../services/claude';
import type {
  CoverLetter,
  CoverLetterParams,
  CvChanges,
  CvScore,
  FeatureKey,
  GeneratedCv,
  LetterChanges,
  LinkedinAudit,
  OutputLanguage,
  RewrittenSection,
} from '../types';

/**
 * Encapsule les appels IA : états loading / error / quota.
 * Quand un quota est dépassé, `quotaFeature` est renseigné → ouvrir le Paywall (Phase 2).
 * Les fonctions renvoient le résultat, ou null en cas d'erreur/quota.
 */
export function useClaude() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaFeature, setQuotaFeature] = useState<FeatureKey | null>(null);
  // Offre hors de portée du CV : `error` porte le message, ceci la raison et le droit d'afficher
  // l'action « modifier l'annonce ».
  const [refusProfil, setRefusProfil] = useState<{ raison: string | null } | null>(null);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setLoading(true);
    setError(null);
    setQuotaFeature(null);
    setRefusProfil(null);
    try {
      return await fn();
    } catch (e) {
      if (e instanceof QuotaError) {
        setQuotaFeature(e.feature);
        // Un abonné Pro ou une licence école n'ont RIEN au-dessus : on ne leur ouvre pas la page
        // des abonnements, on leur dit ce qui se passe et quand le compteur repart.
        if (proposeAbonnement(e.plan)) {
          usePaywallStore.getState().open(e.feature, e.reason);
        } else {
          setError(messageRefusSansOffre(e.reason));
        }
      } else {
        if (e instanceof ProfilTropEloigneError) setRefusProfil({ raison: e.raison });
        setError(e instanceof Error ? e.message : 'Une erreur est survenue.');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // `stage` traverse le hook sans qu'il ait à le comprendre : c'est l'écran qui décide de découper,
  // et le serveur qui retombe sur la réponse complète si le paramètre est absent.
  const scoreLinkedin = useCallback(
    (
      input: LinkedinInput,
      outputLanguage: OutputLanguage = 'fr',
      stage?: claude.LinkedinStage,
    ): Promise<LinkedinAudit | null> => run(() => claude.scoreLinkedin(input, outputLanguage, stage)),
    [run],
  );

  const auditLinkedinAccount = useCallback(
    (outputLanguage: OutputLanguage = 'fr', stage?: claude.LinkedinStage): Promise<LinkedinAudit | null> =>
      run(() => claude.auditLinkedinAccount(outputLanguage, stage)),
    [run],
  );

  const scoreCv = useCallback(
    (input: CvScoreInput): Promise<CvScore | null> => run(() => claude.scoreCv(input)),
    [run],
  );

  const generateCoverLetter = useCallback(
    (input: { cv: string; annonce: CvOfferInput; params: CoverLetterParams }): Promise<CoverLetter | null> =>
      run(() => claude.generateCoverLetter(input)),
    [run],
  );

  const generateCv = useCallback(
    (answers: string[], outputLanguage: OutputLanguage = 'fr'): Promise<GeneratedCv | null> =>
      run(() => claude.generateCv(answers, outputLanguage)),
    [run],
  );

  const extractCv = useCallback(
    (input: CvScoreInput): Promise<GeneratedCv | null> => run(() => claude.extractCv(input)),
    [run],
  );

  const rewriteSection = useCallback(
    (section: string, content: string): Promise<RewrittenSection | null> =>
      run(() => claude.rewriteSection(section, content)),
    [run],
  );

  const improveCvQuestions = useCallback(
    (input: CvImproveInput): Promise<ImproveQuestions | null> =>
      run(() => claude.improveCvQuestions(input)),
    [run],
  );

  const improveCvGenerate = useCallback(
    (cvText: string, answers: ImproveAnswer[], offer?: CvOfferInput): Promise<GeneratedCv | null> =>
      run(() => claude.improveCvGenerate(cvText, answers, offer)),
    [run],
  );

  const improveCvChanges = useCallback(
    (input: CvImproveInput, offer?: CvOfferInput): Promise<CvChanges | null> =>
      run(() => claude.improveCvChanges(input, offer)),
    [run],
  );

  const improveCoverLetter = useCallback(
    (input: claude.LetterImproveInput): Promise<LetterChanges | null> => run(() => claude.improveCoverLetter(input)),
    [run],
  );

  const reset = useCallback(() => {
    setError(null);
    setQuotaFeature(null);
    setRefusProfil(null);
  }, []);

  return {
    loading,
    error,
    refusProfil,
    generateCoverLetter,
    quotaFeature,
    scoreLinkedin,
    auditLinkedinAccount,
    scoreCv,
    generateCv,
    extractCv,
    rewriteSection,
    improveCvQuestions,
    improveCvGenerate,
    improveCvChanges,
    improveCoverLetter,
    reset,
  };
}
